import { useState, useEffect, useRef } from 'react'
import { Modal, Button, Spinner, Alert } from 'react-bootstrap'
import client from '../api/client'

function DeviceCodeAuthModal({ show, onHide, account, apiBasePath, onSuccess }) {
  const [step, setStep] = useState('idle')
  const [deviceInfo, setDeviceInfo] = useState(null)
  const [error, setError] = useState('')
  const pollIntervalRef = useRef(null)

  useEffect(() => {
    if (show && account) {
      startFlow()
    }
    return () => stopPolling()
  }, [show, account])

  const stopPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current)
      pollIntervalRef.current = null
    }
  }

  const handleClose = () => {
    stopPolling()
    setStep('idle')
    setDeviceInfo(null)
    setError('')
    onHide()
  }

  const startFlow = async () => {
    setStep('starting')
    setError('')
    setDeviceInfo(null)

    try {
      const resp = await client.post(`${apiBasePath}/${account.id}/auth/start`)
      setDeviceInfo(resp.data)
      setStep('waiting')
      startPolling(resp.data)
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors du démarrage de l\'authentification')
      setStep('error')
    }
  }

  const startPolling = (info) => {
    const interval = (info.interval || 5) * 1000
    const pollBody = info.device_code
      ? { device_code: info.device_code }
      : { device_auth_id: info.device_auth_id, user_code: info.user_code }

    pollIntervalRef.current = setInterval(async () => {
      try {
        const resp = await client.post(`${apiBasePath}/${account.id}/auth/poll`, pollBody)
        if (resp.data.status === 'completed') {
          stopPolling()
          setStep('success')
          onSuccess(account)
        } else if (resp.data.status === 'error') {
          stopPolling()
          setError(resp.data.error || 'Erreur d\'authentification')
          setStep('error')
        }
      } catch (err) {
        stopPolling()
        setError('Erreur lors de la vérification')
        setStep('error')
      }
    }, interval)
  }

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Authentification OAuth</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {step === 'starting' && (
          <div className="text-center py-4">
            <Spinner animation="border" className="mb-3" />
            <p>Démarrage du flux d'authentification...</p>
          </div>
        )}

        {step === 'waiting' && deviceInfo && (
          <div className="text-center py-3">
            <p className="mb-3">
              Ouvrez le lien ci-dessous et entrez le code :
            </p>
            <div className="mb-3">
              <a
                href={deviceInfo.verification_url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-outline-primary btn-sm"
              >
                {deviceInfo.verification_url}
              </a>
            </div>
            <div
              className="p-3 mb-3 rounded"
              style={{
                backgroundColor: '#f8f9fa',
                border: '2px dashed #6c757d',
                fontSize: '2rem',
                fontWeight: 'bold',
                letterSpacing: '0.3em',
                fontFamily: 'monospace',
              }}
            >
              {deviceInfo.user_code}
            </div>
            <div className="d-flex align-items-center justify-content-center text-muted">
              <Spinner animation="border" size="sm" className="me-2" />
              En attente de validation...
            </div>
          </div>
        )}

        {step === 'success' && (
          <Alert variant="success" className="mb-0">
            Authentification réussie pour <strong>{account?.email}</strong>.
          </Alert>
        )}

        {step === 'error' && (
          <Alert variant="danger" className="mb-0">
            {error}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        {step === 'error' && (
          <Button variant="primary" onClick={startFlow}>
            Réessayer
          </Button>
        )}
        <Button variant="secondary" onClick={handleClose}>
          {step === 'success' ? 'Fermer' : 'Annuler'}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default DeviceCodeAuthModal
